import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useToggle } from '../../hooks/common';
import { Bem } from '../../service/Bem/Bem';
import { DictionaryItem } from '../../typings/common';
import { ExtendedTagValue, TagInputType, TagValue } from '../../typings/tagCloudTargeting';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';
import { TextInputDecorator } from '../TextInputDecorator/TextInputDecorator';

import { EditableTag } from './EditableTag/EditableTag';
import { TagAdd } from './TagAdd/TagAdd';
import './TagCloud.css';

const BLOCK = 'tag-cloud';
const DEFAULT_ICON_SIZE = 16;

export interface TagCloudProps {
    tags: ExtendedTagValue[];
    isEditable: boolean;
    onAdd: (tags: TagValue[]) => void;
    onDelete: (tag: TagValue) => void;
    onChange: (tag: ExtendedTagValue, newValue: TagValue) => void;
    icon?: Shape2Icon;
    iconSize?: number;
    tagPrefix?: string;
    dictionaryItems?: DictionaryItem[];
    inputType?: TagInputType;
    showUserValueOnEdit?: boolean;
}

const getTagKey = (value: ExtendedTagValue[], index: number, isEditable: boolean): string => {
    return `tag_${index}_${value.length}_${isEditable.toString()}`;
};

export const TagCloud: React.FC<TagCloudProps> = React.memo(props => {
    const {
        tags,
        isEditable,
        tagPrefix,
        onAdd,
        onDelete,
        onChange,
        icon,
        iconSize = DEFAULT_ICON_SIZE,
        dictionaryItems,
        showUserValueOnEdit,
        inputType
    } = props;
    const [editableTagIndex, setEditableTagIndex] = useState(-1);
    const [isFocused, focus, blur] = useToggle(isEditable && editableTagIndex !== -1);
    const [inputRef, setInputRef] = useState<React.RefObject<TextInputDecorator>>();

    const bemMods = {
        focused: isFocused,
        editable: isEditable
    };

    const onClick = useCallback(
        (evt: React.MouseEvent) => {
            if (isEditable && editableTagIndex === -1) {
                setEditableTagIndex(tags.length);

                if (inputRef && inputRef.current) {
                    inputRef.current.focus();
                    focus();
                }
            }
        },
        [isEditable, inputRef, tags.length, editableTagIndex]
    );

    const onChangeEditableState = useCallback(() => {
        setEditableTagIndex(tags.length);
    }, [tags.length]);

    const onTagAdd = useCallback(
        (tag: TagValue, isTriggeredByBlur?: boolean) => {
            if (isTriggeredByBlur) {
                setEditableTagIndex(-1);
                blur();
            } else {
                setEditableTagIndex(editableTagIndex + 1);
            }
            onAdd([tag]);
        },
        [tags.length, onAdd, editableTagIndex]
    );

    const onTagsPaste = useCallback(
        (addedTags: TagValue[]) => {
            setEditableTagIndex(tags.length + addedTags.length);
            onAdd(addedTags);
        },
        [tags, onAdd]
    );

    const onTagEdit = useCallback(
        (newValue: TagValue) => {
            setEditableTagIndex(-1);
            onChange(tags[editableTagIndex], newValue);
        },
        [editableTagIndex]
    );

    const onTagDelete = useCallback(
        (index: number) => {
            if (tags[index]) {
                onDelete(tags[index].value);
            }
            setEditableTagIndex(-1);
        },
        [tags, onDelete]
    );

    const onTagClick = useCallback(
        (index: number) => {
            if (isEditable && editableTagIndex !== index) {
                setEditableTagIndex(index);
            }
        },
        [isEditable, onTagDelete, editableTagIndex]
    );

    const onRefChange = useCallback((ref: React.RefObject<TextInputDecorator>) => {
        if (ref) {
            setInputRef(ref);
        }
    }, []);

    const onCancel = useCallback(() => {
        setEditableTagIndex(-1);
        blur();
    }, []);

    useEffect(() => {
        if (editableTagIndex === -1) {
            blur();
        } else {
            focus();
        }
    }, [editableTagIndex, tags.length]);

    return (
        <div className={Bem.cls({ block: BLOCK, mods: bemMods })}>
            {icon && <Shape2 cls={Bem.cls(BLOCK, 'icon')} width={iconSize} height={iconSize} icon={icon} />}

            <div className={Bem.cls(BLOCK, 'value', bemMods)} onClick={onClick}>
                {tags.map((tag, index) => {
                    const isOpenForEdit = isEditable && editableTagIndex === index;
                    const key = getTagKey(tags, index, isOpenForEdit);

                    return (
                        <EditableTag
                            tag={tag}
                            inputType={inputType}
                            prefix={tagPrefix}
                            index={index}
                            key={key}
                            onDelete={onTagDelete}
                            onClick={onTagClick}
                            isOpenForEdit={isOpenForEdit}
                            suggestItems={dictionaryItems}
                            showUserValueOnEdit={showUserValueOnEdit}
                            onSubmit={onTagEdit}
                            onFocus={focus}
                            onCancel={onCancel}
                            onRefChange={onRefChange}
                        />
                    );
                })}

                {isEditable && (
                    <TagAdd
                        isInputShown={editableTagIndex >= tags.length}
                        index={tags.length}
                        inputType={inputType}
                        suggestItems={dictionaryItems}
                        onAddButtonClick={onChangeEditableState}
                        onTagAdd={onTagAdd}
                        onFocus={focus}
                        onCancel={onCancel}
                        onRefChange={onRefChange}
                        onPaste={onTagsPaste}
                    />
                )}
            </div>
        </div>
    );
});
